import { Injectable, inject } from '@angular/core';

import { CategoryRestrictionsProfileConfigurationService } from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, from } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { getAncestors } from '../router/category';
import {
  CategoryRestrictionProfilePresetConfigurationApiActions,
  CategoryRestrictionProfilePresetConfigurationResolverActions,
  CategoryRestrictionProfilePresetConfigurationViewActions,
} from './category-restriction-profile-preset-configuration.actions';

@Injectable()
export class CategoryRestrictionProfilePresetConfigurationEffects {
  private readonly actions$ = inject(Actions);
  private readonly categoryRestrictionsApi = inject(CategoryRestrictionsProfileConfigurationService);

  /**
   * Restriction profile configuration has been selected for a category.
   * Effect: we add the restriction profile configuration to the category via the API.
   */
  restrictionProfileConfigurationSelectedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryRestrictionProfilePresetConfigurationViewActions.restrictionProfileConfigurationSelected),
      mergeMap((action) => {
        const category = action.category;
        const categoryType = category.categoryType;

        return safeMap(
          this.categoryRestrictionsApi.putRestrictionsProfileToCategory({
            categoryType,
            categoryId: category.categoryId,
            setRestrictionsProfilesForCategoryCommand: {
              restrictionsProfilePresets: action.profiles.map((x) => ({
                restrictionsProfilePresetId: x.restrictionsProfilePresetId,
                models: Array.from(x.models),
              })),
            },
          }),
          (configurations) =>
            CategoryRestrictionProfilePresetConfigurationApiActions.restrictionProfileConfigurationSet({
              category,
              configurations,
            }),
          (error) =>
            CategoryRestrictionProfilePresetConfigurationApiActions.restrictionProfileConfigurationSetFailed({ error })
        );
      })
    );
  });

  /**
   * Restriction profile configurations have been requested for a category (resolver).
   * Effect: we load the restriction profile configurations from the API.
   */
  restrictionProfileConfigurationsForCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CategoryRestrictionProfilePresetConfigurationResolverActions.categoryRestrictionProfileConfigurationRequested
      ),
      filter((action) => !!action.category),
      mergeMap((action) => {
        const ancestors = getAncestors(action.category);
        return from(
          ancestors.map((ancestor) => {
            const categoryType = ancestor.categoryType;

            return safeMap(
              this.categoryRestrictionsApi.getRestrictionsProfilesForCategory({
                categoryType,
                categoryId: ancestor.categoryId,
                models: action.modelNames,
              }),
              (apiConfigurations) =>
                CategoryRestrictionProfilePresetConfigurationApiActions.restrictionProfileConfigurationLoaded({
                  configurations: apiConfigurations,
                }),
              (error) =>
                CategoryRestrictionProfilePresetConfigurationApiActions.restrictionProfileConfigurationLoadedFailed({
                  error,
                })
            );
          })
        );
      }),
      mergeMap((actions) => actions)
    );
  });
}
